import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Phone, KeyRound, Loader2 } from 'lucide-react';
import { markDriverSignedIn, API } from '../lib/driverAuth';
import PhoneInput from '../components/ui/phone-input';

const DriverLogin = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextRaw = params.get('next') || '/driver';
  // Defence: if the captured "next" points back at /driver/login (cascade), bounce to /driver
  const next = nextRaw.startsWith('/driver/login') ? '/driver' : nextRaw;

  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!phone || !pin) {
      toast.error('Enter your phone and PIN');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/driver/auth/login`, { phone, pin }, { withCredentials: true });
      markDriverSignedIn(true);
      toast.success(`Welcome ${data.driver?.name?.split(' ')[0] || ''}`.trim());
      navigate(next, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sign-in failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-5 py-12">
      <div className="w-full max-w-sm">
        <div className="text-center mb-10">
          <p className="text-white/40 uppercase tracking-[0.4em] text-xs mb-3">Obsidian</p>
          <h1 className="text-white text-3xl font-bold tracking-tight">Driver Sign-In</h1>
          <p className="text-white/50 text-sm mt-2">Enter your phone number and 4-digit PIN</p>
        </div>

        <form onSubmit={submit} className="bg-white rounded-3xl p-7 shadow-2xl space-y-5" data-testid="driver-login-form">
          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-neutral-500 mb-2">
              <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> Phone</span>
            </label>
            <PhoneInput
              value={phone}
              onChange={setPhone}
              className="bg-white border-neutral-300 text-black h-12 text-lg"
              data-testid="dl-phone"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-neutral-500 mb-2">
              <span className="inline-flex items-center gap-1.5"><KeyRound className="h-3.5 w-3.5" /> PIN</span>
            </label>
            <input
              type="password"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              className="w-full h-12 px-4 rounded-md border border-neutral-300 bg-white text-black text-lg tracking-[0.4em] text-center font-mono"
              placeholder="••••"
              data-testid="dl-pin"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full h-12 rounded-md bg-black text-white font-semibold hover:bg-neutral-900 disabled:opacity-60 flex items-center justify-center gap-2"
            data-testid="dl-submit"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {submitting ? 'Signing in…' : 'Sign In'}
          </button>

          <p className="text-center text-xs text-neutral-500 pt-2">
            PIN trouble? Contact dispatch on 0333 335 7733.
          </p>
          <p className="text-center text-sm text-neutral-600 pt-2 border-t border-neutral-100 mt-2">
            New driver?{' '}
            <Link to="/driver/signup" className="text-black font-semibold underline underline-offset-2" data-testid="dl-go-signup">
              Sign up
            </Link>
          </p>
        </form>

        <p className="text-center text-[11px] text-white/30 mt-8 uppercase tracking-wider">
          Confidential · Authorised drivers only
        </p>
      </div>
    </div>
  );
};

export default DriverLogin;
